<template>
  <div class="one-template">
    <!-- Название проекта -->
    <div class="one-template__header">
      <BaseTypography with-pre-line type="h2">
        {{ data.title }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.description }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.header"
        :button="buttons.header"
      />
    </div>

    <div v-if="data.images.hero" class="one-template__hero-image">
      <TemplateMediaView
        :src="data.images.hero"
        :aspect-width="1312"
        :aspect-height="400"
      />
    </div>

    <div class="one-template__section">
      <BaseTypography with-pre-line :nowrap="false" type="h3">
        {{ data.sectionTitle }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.sectionDescription }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.section"
        :button="buttons.section"
      />
    </div>

    <!-- Два блока с изображениями в строку -->
    <div class="one-template__image-row">
      <div class="one-template__image-block">
        <TemplateMediaView
          v-if="data.images.sectionLeft"
          :src="data.images.sectionLeft ?? '/projects/project-template-2-bg-4.webp'"
          :aspect-width="642"
          :aspect-height="280"
        />
      </div>
      <div class="one-template__image-block">
        <TemplateMediaView
          v-if="data.images.sectionRight"
          :src="data.images.sectionRight ?? '/projects/project-template-2-bg-5.webp'"
          :aspect-width="642"
          :aspect-height="280"
        />
      </div>
    </div>

    <!-- Блоки контента друг напротив друга -->
    <div class="one-template__unified-content">
      <!-- Блок с заголовком и текстом слева -->
      <div class="one-template__unified-block">
        <BaseTypography with-pre-line type="h3">
          {{ data.leftBlockTitle }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.leftBlockText }}
        </BaseTypography>
        <TemplateLandingButtonView
          v-if="buttons.leftBlock"
          full-width
          :button="buttons.leftBlock"
        />
        <TemplateMediaView
          v-if="data.images.leftBlock"
          :src="data.images.leftBlock ?? '/projects/project-template-2-bg-3.webp'"
          :aspect-width="642"
          :aspect-height="320"
        />
      </div>

      <!-- Блок с заголовком и текстом справа -->
      <div class="one-template__unified-block">
        <TemplateMediaView
          v-if="data.images.rightBlock"
          :src="data.images.rightBlock ?? '/projects/project-template-2-bg-4.webp'"
          :aspect-width="642"
          :aspect-height="320"
        />
        <BaseTypography with-pre-line type="h3">
          {{ data.rightBlockTitle }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.rightBlockText }}
        </BaseTypography>
        <TemplateLandingButtonView
          v-if="buttons.rightBlock"
          full-width
          :button="buttons.rightBlock"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IOneTemplateData, TemplateData } from '~/entities/projects/model/templates-interface'
import { computed } from 'vue'
import { useTemplateViewButtons } from '~/entities/projects/model/useTemplateViewButtons'
import TemplateLandingButtonView from '~/entities/projects/ui/templates/TemplateLandingButtonView.vue'
import TemplateMediaView from '~/entities/projects/ui/templates/TemplateMediaView.vue'
import { BaseTypography } from '~/shared/ui'

interface Props {
  templateData?: TemplateData | null
}

const props = withDefaults(defineProps<Props>(), {
  templateData: null,
})

const data = computed(() => {
  if (props.templateData) {
    return props.templateData as IOneTemplateData
  }

  return {
    title: 'Как заполнить этот шаблон',
    description: 'Заголовок — напишите короткую фразу из 3–7 слов. Она должна быть простой и понятной. Заголовок должен передавать позитивное настроение. Например: про новый день, вдохновение, движение вперёд, радость, развитие. Не используйте сложные слова и длинные предложения.',
    sectionTitle: 'Подзаголовок',
    sectionDescription: 'Добавьте одно короткое предложение, которое раскрывает смысл заголовка. Это может быть продолжение мысли или лёгкая мотивационная фраза. Пишите естественно, как будто обращаетесь к обычному человеку. Избегайте сложных слов и пафоса.',
    leftBlockTitle: 'Подзаголовок',
    leftBlockText: 'Напишите текст объёмом до 1000 символов. Лучше разделить его на 2–3 абзаца, чтобы текст было удобно читать. Один абзац — это 2–4 предложения.\nПишите простым языком. Представьте, что вы объясняете мысль другу. Текст должен быть лёгким, понятным и нейтральным. Он не должен быть привязан к конкретному продукту, компании или услуге. Это универсальный текст, который используется как пример для шаблона.',
    rightBlockTitle: 'Подзаголовок',
    rightBlockText: 'Можно писать о вдохновении, новых возможностях, развитии, спокойствии, ценности момента, движении вперёд или простых радостях жизни.\nСтарайтесь: не использовать сложные профессиональные термины, не добавлять агрессивную рекламу, не писать слишком длинные предложения, не перегружать текст цифрами и фактами.\nВажно, чтобы текст выглядел аккуратно в блоке и легко воспринимался при быстром чтении.',
    images: {
      hero: '/projects/project-template-2-bg-1.webp',
      sectionLeft: '/projects/project-template-2-bg-2.webp',
      sectionRight: '/projects/project-template-2-bg-3.webp',
      leftBlock: '/projects/project-template-2-bg-4.webp',
      rightBlock: '/projects/project-template-2-bg-5.webp',
    },
  }
})

const buttons = useTemplateViewButtons(() => props.templateData)
</script>

<style lang="scss" scoped>
.one-template {
  --hero-image-max-width: 100%;
  --section-image-max-width: 100%;
  --block-image-max-width: 100%;

  display: grid;
  gap: rem(60px);

  @include bp-media('xs', 'sm', 'md') {
    gap: rem(40px);
  }

  > * {
    min-width: 0;
  }

  &__header {
    width: 100%;
    display: grid;
    gap: rem(16px);

    > * {
      min-width: 0;
    }
  }

  &__section {
    display: grid;
    gap: rem(16px);

    > * {
      min-width: 0;
    }
  }

  &__image-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__unified-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__unified-block {
    display: grid;
    gap: rem(16px);

    > * {
      min-width: 0;
    }
  }

  &__hero-image {
    overflow: hidden;
    border-radius: rem(16px);
    display: grid;
  }

  &__image-block {
    overflow: hidden;
    border-radius: rem(16px);
    display: grid;
  }

  :deep(.base-typography) {
    word-break: break-word;
    overflow-wrap: break-word;
  }
}
</style>
